import {
  WorkflowStartNodeData,
  WorkflowAllTriggeredByWorkflowsData,
  TriggeredByCustomEventStartNodeData,
  TriggeredByListStartNodeData,
  TriggeredByChannelStartNodeData,
  TriggeredByCampaignStartNodeData,
  TriggeredByFieldStartNodeData,
} from '../types';
import {
  WORKFLOW_EVENTS_TRIGGERED_BY_LIST,
  WORKFLOW_TRIGGER_EVENTS,
  WORKFLOW_EVENTS_TRIGGERED_BY_CHANNEL,
  WORKFLOW_EVENTS_TRIGGERED_BY_CAMPAIGN,
  WORKFLOW_EVENTS_TRIGGERED_BY_FIELD,
  WORKFLOW_EVENTS_TRIGGERED_BY_CUSTOM_EVENT,
  WORKFLOW_EVENTS_TRIGGERED_BY_JOURNEY,
} from '../constants';
import { getTriggerNodeDataTypeNameFromEvent } from './getTriggerNodeDataTypeNameFromEvent';

const isWorkflowTriggeredByEvents = (
  workflow: WorkflowStartNodeData,
  events: ReadonlyArray<WORKFLOW_TRIGGER_EVENTS>,
) =>
  !!events.find(
    (event) =>
      getTriggerNodeDataTypeNameFromEvent(event) === workflow.startNode?.nodeData.__typename,
  );
const shouldUseCustomEventName = (
  workflow: WorkflowStartNodeData,
): workflow is TriggeredByCustomEventStartNodeData =>
  isWorkflowTriggeredByEvents(workflow, WORKFLOW_EVENTS_TRIGGERED_BY_CUSTOM_EVENT);
const shouldUseEmailListName = (
  workflow: WorkflowStartNodeData,
): workflow is TriggeredByListStartNodeData =>
  isWorkflowTriggeredByEvents(workflow, WORKFLOW_EVENTS_TRIGGERED_BY_LIST);
const shouldUseChannelName = (
  workflow: WorkflowStartNodeData,
): workflow is TriggeredByChannelStartNodeData =>
  isWorkflowTriggeredByEvents(workflow, WORKFLOW_EVENTS_TRIGGERED_BY_CHANNEL);
const shouldUseCampaignName = (
  workflow: WorkflowStartNodeData,
): workflow is TriggeredByCampaignStartNodeData =>
  isWorkflowTriggeredByEvents(workflow, WORKFLOW_EVENTS_TRIGGERED_BY_CAMPAIGN);
const shouldUseFieldName = (
  workflow: WorkflowStartNodeData,
): workflow is TriggeredByFieldStartNodeData =>
  isWorkflowTriggeredByEvents(workflow, WORKFLOW_EVENTS_TRIGGERED_BY_FIELD);
const shouldUseAllTriggeredByWorkflows = (
  workflow: WorkflowStartNodeData,
): workflow is WorkflowStartNodeData =>
  isWorkflowTriggeredByEvents(workflow, WORKFLOW_EVENTS_TRIGGERED_BY_JOURNEY);

const getTriggeredByJourneyTooltip = (workflows: WorkflowAllTriggeredByWorkflowsData[]) => {
  const text = workflows.reduce(
    (acc: string, { workflow: wf }: WorkflowAllTriggeredByWorkflowsData, idx: number) => {
      const separator = idx > 1 ? ', ' : '';
      const item = `${wf.name} (${wf.id})`;
      return idx > 0 ? `${acc}${separator}${item}` : '';
    },
    '',
  );
  const others = workflows.length - 1;
  return {
    text,
    triggerText: `+${others} other${others > 1 ? 's' : ''}`,
  };
};

/**
 * Format the resource name to show the name and id if available
 * @example
 * formatResourceName('My List', 123) => 'My List (123)'
 * formatResourceName('My List', null) => 'My List'
 * formatResourceName(null, 123) => '(123)'
 * formatResourceName(null, null) => ''
 */
const formatResourceName = (name: string | null, id?: string | number | null) => {
  const textArr = [];
  if (name) textArr.push(name);
  if (id) textArr.push(`(${id})`);
  return textArr.join(' ');
};

export const getWorkflowTriggerResourceName = (workflow: WorkflowStartNodeData) => {
  const { allTriggeredByWorkflows } = workflow;
  let resourceName = null;
  let resourceTooltip = null;

  if (shouldUseCustomEventName(workflow)) {
    resourceName = formatResourceName(workflow.startNode.nodeData.eventName);
  } else if (shouldUseEmailListName(workflow)) {
    resourceName = formatResourceName(
      workflow.startNode.resourceName,
      workflow.startNode.nodeData.emailListId,
    );
  } else if (shouldUseChannelName(workflow)) {
    resourceName = formatResourceName(
      workflow.startNode.resourceName,
      workflow.startNode.nodeData.channelId,
    );
  } else if (shouldUseCampaignName(workflow)) {
    resourceName = formatResourceName(
      workflow.startNode.resourceName,
      workflow.startNode.nodeData.emailCampaignId,
    );
  } else if (shouldUseFieldName(workflow)) {
    resourceName = formatResourceName(workflow.startNode.nodeData.field);
  } else if (shouldUseAllTriggeredByWorkflows(workflow) && allTriggeredByWorkflows?.length) {
    const { workflow: wf } = allTriggeredByWorkflows[0];
    resourceName = formatResourceName(wf.name, wf.id);
    if (allTriggeredByWorkflows.length > 1) {
      resourceTooltip = getTriggeredByJourneyTooltip(allTriggeredByWorkflows);
    }
  }

  return { resourceName, resourceTooltip };
};
